import * as React from 'react';

import { staffFor, type IDemoStaff } from '../fixtures';
import { MOCK_ACCESS, holds, type DemoRole, type MockScreen } from '../roles';
import type { SurveyType } from '../vocabulary';
import type { DemoAction } from './actions';
import { demoReducer, initialDemoState, type IDemoState } from './root';

/**
 * The demo's state for the whole app, in memory: mounted at the root beside the routes only while
 * the demo is on, so no mock screen, no mock panel and the guide all see one state, and moving
 * between the two portals keeps it. Nothing is written to storage and nothing is sent: a reload is
 * a reset, and "Reset demo" is the same thing without one.
 */
interface IDemoContext {
  state: IDemoState;
  dispatch: React.Dispatch<DemoAction>;
}

const DemoContext = React.createContext<Nullable<IDemoContext>>(null);

export function DemoStateProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(demoReducer, undefined, initialDemoState);
  const value = React.useMemo(() => ({ state, dispatch }), [state]);
  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

/** The state and its dispatch. Only a mock renders this, and a mock renders only with the demo on,
 *  under the provider — so a missing provider is a wiring mistake, said loudly. */
export function useDemo(): IDemoContext {
  const context = React.useContext(DemoContext);
  if (!context) throw new Error('A demo screen rendered outside DemoStateProvider.');
  return context;
}

/** The same, or null with the demo off — for the few live components that ask whether a mock of
 *  theirs is shown, and render with or without the demo. */
export function useDemoOptional(): Nullable<IDemoContext> {
  return React.useContext(DemoContext);
}

export interface IMockGrant {
  /** Whether the viewer, as the role viewed as, opens the screen at all. */
  opens: boolean;
  /** Whether its controls act for them. */
  acts: boolean;
  role: Nullable<DemoRole>;
}

/**
 * What the role being viewed as may do on one mock screen, by the demo’s role matrix
 * (`ROLE_MATRIX`). Viewing as signed in, every mock opens and acts: the real guards have already
 * decided the screen may be drawn, and "View as role" only ever narrows the mock half.
 */
export function useMockGrant(screen: MockScreen): IMockGrant {
  const { state } = useDemo();
  const role = state.viewAs;
  if (!role) return { opens: true, acts: true, role };
  const access = MOCK_ACCESS[screen];
  const opens = access.opens === null || holds(role, access.opens);
  const acts = opens && (!access.acts || holds(role, access.acts));
  return { opens, acts, role };
}

/** The cast member a change is made as — the role viewed as, or the first analyst — and the name
 *  the records' histories carry. */
export function useActingAs(): IDemoStaff {
  const { state } = useDemo();
  return staffFor(state.viewAs) ?? staffFor(null)!;
}

